/** 입력 제약. **apps/api 와 클라이언트가 같은 값을 봐야 한다** —
 * 서버가 415/413/409 로 거절할 것을 클라이언트가 미리 알아야 셀러가 8장을 다 올린 뒤에
 * 실패를 보지 않는다.
 *
 * **막는 값이 셋이다**: 형식(`ALLOWED_CONTENT_TYPES`) · 장당 용량(`DEFAULT_MAX_BYTES`) ·
 * **작업당 장 수**(`MAX_IMAGES_PER_JOB`). 앞 둘은 파일 하나를 보고 판정하므로
 * `checkUploadCandidate` 가 답하고, 마지막 하나는 **그 작업에 이미 몇 장 있는지**를 알아야
 * 해서 `remainingImageSlots` 가 따로 답한다. 한 함수에 담지 않은 이유가 그 입력 차이다.
 *
 * 원래 `apps/api/src/routes/jobs.ts` 안에 있던 상수를 여기로 올렸다. 앱이 다시 적으면
 * 두 벌이 되고, 서버 상한이 바뀌었을 때 앱만 옛 값을 쓰게 된다.
 */

/** provider 가 받는 형식. HEIC 가 없다 — 아이폰 앨범 사진 대부분이 HEIC 이므로
 * **클라이언트가 업로드 전에 변환해야 한다**(PRD 7.6.3 하단 힌트, 13. O-3 A안). */
/* `ReadonlySet` — 서버의 업로드 가드가 이 값을 그대로 읽어서, `.add()` 가 열려 있으면
 * 화면 코드 한 줄이 서버가 받는 형식을 넓힌다. */
export const ALLOWED_CONTENT_TYPES: ReadonlySet<string> = new Set<string>([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

/** 상용 API 가 장당 과금이라 거대한 파일을 그대로 넘기면 원가가 샌다.
 * 서버는 `MAX_IMAGE_BYTES` 환경변수로 덮어쓸 수 있고, 이 값은 그 기본값이다. */
export const DEFAULT_MAX_BYTES = 15 * 1024 * 1024;

/** 권장 장수. **상한이 아니다** — 9장째도 막지 않고 "9 / 8장"처럼 그대로 센다.
 * 막는 값은 바로 아래 `MAX_IMAGES_PER_JOB`(15) 하나뿐이다. */
export const RECOMMENDED_IMAGE_COUNT = 8;

/** **한 작업에 담을 수 있는 사진 수의 상한.** 서버가 16장째를 409 `TOO_MANY_IMAGES` 로
 *  거절한다(`api.ts` 의 `ApiErrorCode`).
 *
 * 값의 출처는 **2026-09-23 사용자 확정**이고 원문은 `15장` 한 마디다.
 * ⚠️ **`docs/PRD.md` 가 아직 두 말을 하고 있다.** 본문 여러 곳은 `15장` 확정을 인용하는데
 * **`13.1` O-16 의 표 한 줄은 `포함 8장 + 상한 20장`(기본값, 확정 아님)** 그대로다. 20 은
 * PRD 가 스스로 제안하고 스스로 채택한 수이고, 15 는 사용자가 부른 수다. **뒤엣것이 이긴다.**
 * 그 표를 고치는 것은 `prd-manager` 소관이라 여기서 하지 않았다.
 * ⛔ **PRD 에서 20 을 보고 이 값을 되돌리지 마라.** 되돌리려면 사용자에게 묻는다.
 *
 * ## ⛔ 바로 위 `RECOMMENDED_IMAGE_COUNT`(8) 와 다른 축이다. 한 낱말로 섞지 마라
 *
 * | | 값 | 넘으면 | 누가 읽나 |
 * |---|---|---|---|
 * | `RECOMMENDED_IMAGE_COUNT` | **8** | **안 막는다.** `9 / 8장` 처럼 그대로 센다 | 화면의 안내·카운트 |
 * | `MAX_IMAGES_PER_JOB` | **15** | **막는다.** 서버가 409 로 거절한다 | 서버 가드 · 화면 가드 |
 *
 * 8 은 "이만큼이면 상세가 채워진다"는 **권장**이고, 15 는 "여기까지만 받는다"는 **상한**이다.
 * 둘을 한 자리에서 같은 낱말로 부르면 화면이 9장째를 막거나(권장을 상한으로 읽음) 16장째를
 * 통과시킨다(상한을 권장으로 읽음). **`n / 8장` 카운트는 8 을 쓰고, 막는 자리는 15 를 쓴다.**
 *
 * ## ⚠️ 15장이 **한 회차에 끝난다는 뜻이 아니다**
 *
 * 이 상한은 **끝이 있게** 하는 것이지 **한 번에 끝나게** 하는 것이 아니다. 한 회차가 시작할
 * 수 있는 장 수를 정하는 것은 이 값이 아니라 시간 예산이다
 * (`processing-budget.ts`: `CUTOUT_PER_IMAGE_ESTIMATE_MS`(42초) × `CUTOUT_BUDGET_SAFETY_FACTOR`(3)
 * 과 `CUTOUT_MAX_SERVER_BUDGET_MS`(240초)). 그 산수로는 한 회차에 시작하는 장이 **몇 장뿐**
 * 이고, **15장짜리 작업은 여러 회차로 나뉜다.** 그 사정을 셀러에게 말하는 화면이
 * 2026-09-23 에 섰다(`A-24e`. PRD `7.6.7d`).
 *
 * ⛔ **그래서 이 값을 시간 예산의 입력으로 쓰지 마라.** `cutoutServerBudgetMs(15)` 를 미리
 * 계산해 "15장이면 몇 초"라고 적는 자리를 만들면, 회차가 나뉜다는 사실이 그 숫자 뒤로
 * 숨는다. **예산은 이번 회차가 집은 장 수로 부른다.**
 *
 * ## ⛔ 멱등 재시도를 이 상한으로 막지 마라
 *
 * 업로드는 같은 사진을 다시 올려도 행이 늘지 않는다(`(jobId, contentHash)` 유니크.
 * 응답은 200 `duplicate: true`). **이미 담긴 장을 다시 올리는 요청은 장수를 늘리지 않으므로
 * 상한에 걸리면 안 된다.** 15장이 찬 작업에서 15번째 응답이 유실돼 클라이언트가 재시도했을
 * 때 그것을 409 로 거절하면, **서버에는 있는 사진을 화면은 실패로 그린다.** 판정 순서는
 * `중복 조회 → (중복이면 200) → 상한 검사 → 저장` 이다.
 *
 * ## 세는 것은 **행 수**다
 *
 * ⛔ `sortOrder` 로 판정하지 마라. 0-based 라 `sortOrder < 15` 로 대신할 수 있어 보이지만,
 * 순서는 클라이언트가 보내고 재정렬이 통째로 다시 매기는 값이라 조밀하다는 보장이 없다.
 * **정본은 그 작업에 살아 있는 `JobImage` 행의 수다.** */
export const MAX_IMAGES_PER_JOB = 15;

/** 지금 `currentCount` 장이 담긴 작업이 **몇 장을 더 받을 수 있는가.** 서버·웹·앱 **세 곳이
 *  같은 판정을 해야 해서** 여기 둔다(`checkUploadCandidate` 와 같은 이유).
 *
 * - **서버**: `remainingImageSlots(행 수) <= 0` 이면 409 `TOO_MANY_IMAGES`.
 * - **화면**: 여러 장을 한 번에 고른 경우 받을 장 수는
 *   `Math.min(files.length, remainingImageSlots(n))` 이다. 나머지는 보내기 전에 잘라 낸다.
 *   서버까지 보내서 409 를 받으면 **셀러는 다 올린 뒤에 실패를 본다**(이 파일 머리말).
 *
 * ⚠️ **셀러에게 보일 문장은 여기서 짓지 않는다.** 이 함수는 숫자만 돌려주고, 낱말은
 * `design-manager` 가 정한 표를 화면이 쓴다.
 *
 * ⚠️ **이상한 값은 0(더 못 받는다)으로 떨어진다.** `NaN`·`Infinity` 는 "몇 장인지 모른다"는
 * 뜻이고, 모르는 상태에서 15칸을 열어 주는 것보다 막는 쪽이 덜 나쁘다. 음수도 같다.
 * 정수가 아닌 값은 **올려서** 센다(`8.5` → 9장 담긴 것으로). 어느 쪽도 정상 입력이 아니라면
 * **덜 받는 쪽이 덜 나쁘다.** */
export function remainingImageSlots(currentCount: number): number {
  if (!Number.isFinite(currentCount) || currentCount < 0) return 0;
  return Math.max(0, MAX_IMAGES_PER_JOB - Math.ceil(currentCount));
}

/** `acceptWithinImageLimit` 의 답. **두 값을 한 번에 돌려주는 것이 요점이다** — 자른 뒤에
 *  세면 몇 장이 밀렸는지 알 수 없어서 화면이 왜 빠졌는지 말할 수 없다. */
export interface ImageLimitSplit<T> {
  /** 실제로 담기는 것. **넘어온 순서 그대로다**(고른 순서가 곧 상세 순서다). */
  accepted: T[];
  /** **상한이 차서** 밀려난 수.
   *
   * ⛔ **형식·용량으로 거절된 장과 같은 칸에 담지 마라.** `checkUploadCandidate` 가 내는
   * 거절은 **그 파일을 바꿔야 하는 일**이고, 이 수는 **자리를 비우면 되는 일**이다. 화면이
   * 둘을 다른 문장으로 말한다(`…장이 다 차서 담지 못했어요` ↔ `이 형식은 올릴 수 없어요` ·
   * `파일이 너무 커요`. `failure-messages.ts` 의 `uploadRejectMessage`).
   * 한 수로 합치면 셀러는 파일을 고치러 가거나 자리를 비우러 가거나, **둘 중 틀린 쪽으로
   * 간다.** */
  full: number;
}

/** 지금 `currentCount` 장이 담긴 작업에 `incoming` 을 넣으려 할 때,
 *  **자리에 들어가는 것과 밀려난 수를 가른다.**
 *
 * ## 왜 함수인가 (2026-09-24)
 *
 * 웹(`UploadPanel.tsx`)과 앱(`draft-store.ts`)이 **똑같은 세 줄**을 각자 갖게 되는 자리였다.
 * 두 벌이 되면 **한쪽만 고쳐지는 날 갈린다** — 이 저장소가 `7.7.3a` 에 세 번 적어 둔 사고다.
 *
 * ⭐ **그리고 이 자리가 `packages/shared` 여야 시험을 걸 수 있다.** 화면 시험
 * (`scripts/*.test.mjs` → CI `gate.yml`)은 `pnpm install` 없이 `node:` 빌트인만으로 도는데,
 * `draft-store.ts` 는 `react` 의 `useSyncExternalStore` 를 import 하고 `UploadPanel.tsx` 는
 * 컴포넌트 안에 로직이 박혀 있어 **거기서는 못 불린다.** 판정이 순수 TypeScript 인 여기
 * 있으면 시험이 **실제로 불러 돌아온 값**을 단정하고, 화면 안에 있으면 소스 글자를 세는
 * 수밖에 없다(`upload-limit.test.mjs` 머리말의 규율. `result-gate.test.mjs` 선례).
 *
 * ## ⛔ 중복 걸러내기는 여기 없다. 부르는 쪽이 먼저 거른다
 *
 * 앱은 `key` 로, 웹은 서버 해시로 거른다. **성격이 다른 판정이라 한 함수에 못 담는다.**
 * 부르는 쪽이 거른 **뒤의 것**을 넘겨야 「이미 담긴 사진은 자리를 안 먹는다」가 성립한다.
 * 서버의 판정 순서(`중복 조회 → 상한 검사 → 저장`)와 같은 이유이고, 같은 순서다.
 *
 * ## ⛔ 형식·용량 검사도 여기 없다
 *
 * 그쪽은 `checkUploadCandidate` 가 파일 하나를 보고 답한다. 이 함수는 **몇 개인지만 본다** —
 * 그래서 제네릭이다(웹은 `File` 을 감싼 객체를, 앱은 `DraftPhoto` 를 넘긴다). 두 검사를
 * 한 함수에 담지 않은 이유는 이 파일 머리말의 입력 차이 그대로다.
 *
 * ⚠️ **다만 거르는 순서는 부르는 쪽이 정해야 한다.** 형식 거절을 먼저 걸러 넘기면 그 장은
 * 자리를 안 먹고, 그대로 넘기면 자리를 먹은 채 밀린다. **먼저 거르는 쪽이 맞다** — 못 올릴
 * 장이 올릴 수 있는 장의 자리를 뺏으면 안 된다.
 *
 * ⚠️ **셀러에게 보일 문장은 여기서 짓지 않는다.** 숫자와 목록만 돌려주고, 낱말은
 * `failure-messages.ts` 가 준다(`remainingImageSlots` 와 같은 규율).
 *
 * ## 돌려주는 배열은 **언제나 새 배열이다**
 *
 * 하나도 안 밀렸을 때 `incoming` 을 그대로 돌려주면, 부르는 쪽이 그것을 상태에 넣고 나중에
 * 밀어 넣는 순간 **넘긴 쪽 배열까지 바뀐다.** 잘릴 때만 새 배열이 되는 함수는 그 사고가
 * **넘치지 않는 날에만 안 나므로** 늦게 드러난다. 항상 복사한다.
 *
 * ⚠️ **이상한 `currentCount` 는 `remainingImageSlots` 가 0 으로 떨군다** — 그러면 이 함수는
 * 한 장도 안 받고 전부 `full` 로 센다. "몇 장인지 모르는" 상태에서 15칸을 여는 것보다 낫다. */
export function acceptWithinImageLimit<T>(
  incoming: readonly T[],
  currentCount: number,
): ImageLimitSplit<T> {
  // **자리를 먼저 세고 자른다.** `slice` 뒤에 세면 넘친 장 수를 알 수 없다.
  const room = remainingImageSlots(currentCount);
  const accepted = incoming.slice(0, room);
  return { accepted, full: incoming.length - accepted.length };
}

/** **파일 하나를 보고 나는 거절 사유.** `checkUploadCandidate` 가 돌려주는 값이 이것뿐이다.
 *
 * ⛔ **여기에 `JOB_FULL` 을 넣지 마라.** 넣으면 `checkUploadCandidate` 의 반환 타입이 그
 * 값을 광고하는데, 그 함수는 **파일 하나만 보므로 영영 그 값을 낼 수 없다.** 타입이 없는
 * 갈래를 약속하면 호출부는 못 오는 분기를 쓰게 되고, 진짜 그 갈래를 내야 하는 자리
 * (장수 판정)는 여전히 비어 있다. 넓은 쪽은 바로 아래 `UploadBlockReason` 이다. */
export type UploadRejectReason = "UNSUPPORTED_TYPE" | "TOO_LARGE";

/** **화면이 이 장을 못 담은 이유.** 위보다 한 갈래 넓다 — 축이 둘이기 때문이다.
 *
 * | 갈래 | 무엇을 보고 나나 | 누가 판정하나 |
 * |---|---|---|
 * | `UNSUPPORTED_TYPE` · `TOO_LARGE` | **그 파일 하나** | `checkUploadCandidate` |
 * | `JOB_FULL` | **그 작업에 지금 몇 장 있나** | `remainingImageSlots(n) <= 0` |
 *
 * 화면은 두 판정의 결과를 한 칸(`reason`)에 담아 행마다 그리므로 그 칸의 타입이 이것이다.
 * 문장은 셋 다 `uploadRejectMessage` 하나가 준다.
 *
 * ## ⛔ 서버의 `TOO_MANY_IMAGES` 와 **이름이 다른 것은 의도다**
 *
 * 그쪽(`api.ts` 의 `ApiErrorCode`)은 **요청이 409 로 거절된 이유**고, 이쪽은 **그 장이 이
 * 작업에 못 들어간 이유**다. **화면이 보내기 전에 잘라낸 장은 서버에 간 적이 없다** —
 * 그 장에 `TOO_MANY_IMAGES` 를 붙이면 가 본 적 없는 요청이 거절당했다는 뜻이 된다.
 * 같은 저장소에 선례가 둘 있다: `TOO_LARGE`(올리기 전) ↔ `SOURCE_TOO_LARGE`(호출 전),
 * 그리고 `RecallBlockReason` 의 `JOB_PROCESSING` ↔ 서버의 `ALREADY_PROCESSING`
 * (`domain.ts`). **축이 다르면 이름을 가른다.**
 *
 * ⭐ **그래도 셀러가 보는 문장은 한 벌이다.** 화면이 잘라낸 장과 서버가 409 로 거절한 장은
 * 셀러에게 같은 일이라 같은 문장을 쓴다(PRD `7.5.9` 문구 2번 · 판정 4). 이름이 갈린 것은
 * 코드 쪽 사정이고, 그 사정이 화면에 새지 않게 하는 자리가 `uploadRejectMessage` 다. */
export type UploadBlockReason = UploadRejectReason | "JOB_FULL" | "UPLOAD_CLOSED";

export interface UploadCandidate {
  contentType: string;
  byteSize: number;
}

/** 업로드 전 검사. 서버의 두 가드(415 상당 / 413)와 같은 판정을 클라이언트에서 미리 한다. */
export function checkUploadCandidate(
  candidate: UploadCandidate,
  maxBytes: number = DEFAULT_MAX_BYTES,
): UploadRejectReason | null {
  if (!ALLOWED_CONTENT_TYPES.has(candidate.contentType)) return "UNSUPPORTED_TYPE";
  if (candidate.byteSize > maxBytes) return "TOO_LARGE";
  return null;
}

/** 변환이 필요한가. 허용 형식이 아니면 앱이 JPEG 로 재인코딩한 뒤 올린다. */
export function needsTranscode(contentType: string): boolean {
  return !ALLOWED_CONTENT_TYPES.has(contentType);
}

/** 변환 후 목표 형식. 한 곳에서 정해 앱·웹이 같은 형식으로 올린다. */
export const TRANSCODE_TARGET_TYPE = "image/jpeg";
